import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API_URL } from '../utils/api';
import './FriendInviteModal.css';

interface InviterInfo {
  displayName: string;
  level: string;
  stamps: { id: string; name: string }[];
}

interface Props {
  referralCode: string;
  token: string | null;
  onAccepted: () => void;
  onDeclined: () => void;
  onSignInRequired?: () => void;
}

export default function FriendInviteModal({ referralCode, token, onAccepted, onDeclined, onSignInRequired }: Props) {
  const { t } = useTranslation();
  const [inviter, setInviter] = useState<InviterInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [autoAcceptAfterAuth, setAutoAcceptAfterAuth] = useState(false);

  useEffect(() => {
    const base = API_URL || '';
    axios.get(`${base}/api/friends/inviter/${referralCode}`)
      .then(({ data }) => setInviter(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [referralCode]);

  const acceptFriendship = async (authToken: string) => {
    setSubmitting(true);
    try {
      const base = API_URL || '';
      await axios.post(
        `${base}/api/friends/join`,
        { referralCode, accept: true },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      onAccepted();
    } catch {
      onAccepted();
    }
  };

  // Auto-accept after the anonymous user finishes login.
  useEffect(() => {
    if (autoAcceptAfterAuth && token && !submitting) {
      acceptFriendship(token);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, autoAcceptAfterAuth]);

  const handleAccept = async () => {
    if (!token) {
      // Anonymous — open auth flow. Mark intent so we auto-accept once token arrives.
      setAutoAcceptAfterAuth(true);
      onSignInRequired?.();
      return;
    }
    await acceptFriendship(token);
  };

  if (loading) return null;
  if (!inviter) {
    onDeclined();
    return null;
  }

  const initials = inviter.displayName
    .split(' ')
    .map(w => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <div className="fim-backdrop">
      <div className="fim-modal">
        <div className="fim-avatar">{initials}</div>
        <h2 className="fim-title">{inviter.displayName}</h2>
        <p className="fim-subtitle">{inviter.level}</p>

        <p className="fim-desc">
          {t('friends.inviteDesc', 'Приглашает вас в друзья! Став друзьями, вы сможете:')}
        </p>
        <ul className="fim-benefits">
          <li>{t('friends.benefit1', 'Видеть когда друг ест и угощать из меню')}</li>
          <li>{t('friends.benefit2', 'Смотреть фуд-паспорт друг друга')}</li>
          <li>{t('friends.benefit3', 'Получить бонусные баллы за дружбу')}</li>
        </ul>

        {inviter.stamps.length > 0 && (
          <div className="fim-stamps">
            {inviter.stamps.slice(0, 4).map(s => (
              <span key={s.id} className="fim-stamp">{s.name}</span>
            ))}
          </div>
        )}

        <div className="fim-actions">
          <button className="fim-btn fim-btn--accept" onClick={handleAccept} disabled={submitting}>
            {submitting
              ? '...'
              : token
                ? t('friends.accept', 'Добавить в друзья')
                : t('friends.signInToAccept', 'Войти и принять приглашение')}
          </button>
          <button className="fim-btn fim-btn--decline" onClick={onDeclined} disabled={submitting}>
            {t('common.cancel', 'Отмена')}
          </button>
        </div>
      </div>
    </div>
  );
}
